<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  execution: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

// Progresso local que é atualizado
const localProgress = ref(props.execution.progress);

// Timer para atualizar o progresso
let progressInterval = null;

const updateLocalProgress = () => {
  const elapsed = Date.now() - props.execution.startedAt;
  localProgress.value = Math.min((elapsed / props.execution.totalTime) * 100, 100);
};

onMounted(() => {
  progressInterval = setInterval(updateLocalProgress, 50);
});

onUnmounted(() => {
  if (progressInterval) {
    clearInterval(progressInterval);
  }
});

// Tempo restante formatado
const remainingTime = computed(() => {
  const elapsed = Date.now() - props.execution.startedAt;
  const remaining = Math.max(props.execution.totalTime - elapsed, 0);
  
  if (remaining < 1000) {
    return t('MACRO_QUEUE.COMPLETING');
  }
  
  const seconds = Math.ceil(remaining / 1000);
  if (seconds >= 60) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }
  return `${seconds}s`;
});

// Ação atual (baseada no progresso)
const currentAction = computed(() => {
  const actions = props.execution.actions;
  if (!actions || actions.length === 0) return '';
  
  const progress = localProgress.value / 100;
  const actionIndex = Math.min(
    Math.floor(progress * actions.length),
    actions.length - 1
  );
  
  const action = actions[actionIndex];
  return t(`MACROS.ACTIONS.${action.action_name.toUpperCase()}`) || action.action_name;
});

// Status da barra de progresso
const progressColor = computed(() => {
  if (localProgress.value >= 100) {
    return 'bg-green-500';
  }
  return 'bg-woot-500';
});

// Iniciais do contato
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};
</script>

<template>
  <div 
    class="macro-queue-item p-3 bg-white dark:bg-n-solid-2 rounded-lg border border-n-weak/50 shadow-sm transition-all duration-200"
    :class="{ 'opacity-60': localProgress >= 100 }"
  >
    <!-- Header: Nome da macro e contato -->
    <div class="flex items-center gap-3 mb-3">
      <!-- Avatar do contato -->
      <div 
        v-if="execution.contactAvatar"
        class="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
      >
        <img 
          :src="execution.contactAvatar" 
          :alt="execution.contactName" 
          class="w-full h-full object-cover" 
        />
      </div>
      <div 
        v-else
        class="w-8 h-8 rounded-full bg-woot-500 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0"
      >
        {{ getInitials(execution.contactName) }}
      </div>
      
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-n-slate-12 truncate">
          {{ execution.macroName }}
        </p>
        <p class="text-xs text-n-slate-11 truncate">
          {{ execution.contactName }}
        </p>
      </div>
      
      <!-- Tempo restante -->
      <div class="text-xs font-medium text-n-slate-11">
        {{ remainingTime }}
      </div>
    </div>
    
    <!-- Barra de progresso -->
    <div class="relative w-full h-1.5 bg-n-alpha-2 rounded-full overflow-hidden">
      <div 
        class="absolute left-0 top-0 h-full rounded-full transition-all duration-100 ease-out"
        :class="progressColor"
        :style="{ width: `${localProgress}%` }"
      />
    </div>
    
    <!-- Ação atual -->
    <div class="mt-2 flex items-center gap-1.5">
      <span class="i-lucide-loader-2 w-3 h-3 animate-spin text-woot-500" v-if="localProgress < 100" />
      <span class="i-lucide-check w-3 h-3 text-green-500" v-else />
      <span class="text-xs text-n-slate-11">
        {{ currentAction }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.macro-queue-item {
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
